import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import { Calendar } from "./Calendar";
import { useCalendarLabels } from "./useCalendarLabels";
import { useGuestSelectLabels } from "./useGuestSelectLabels";
import { GuestSelect } from "./GuestSelect";
import { AdFormatTabs } from "./AdFormatTabs";
import { Button } from "./Button";
import { Skeleton } from "./Skeleton";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  max as maxDate,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { AgeType } from "../api/contracts/reservation";
import { GuestTypesConfig } from "../api/contracts/site-config";
import { formatGuests } from "../utils/guestFormatter";
import { keepActiveUnit, sumGuestTypesAcrossUnits } from "../utils/guestUnitTotals";
import { useGetCategories } from "../api/api-hooks/category-ads";
import { useGetAvailabilityCalendar } from "../api/api-hooks/availability-calendar";
import { getAvailabilityLoadingState } from "../utils/availabilityLoadingState";
import { keepPreviousData } from "@tanstack/react-query";
import { useHeader, createUnitId } from "../contexts/HeaderContext";
import { useConfiguration } from "../contexts/ConfigurationProvider";
import { useGetReservation } from "../api/api-hooks/reservation";
import { useBookingStore } from "../stores/bookingStore";
import { Guest, SearchCategoryAdsRequest } from "../api/contracts/category-ads";
import {
  splitChildAndInfantGuests,
  transformGuestsForSearch,
} from "../utils/apiUtils";
import { formatDateForAPI, guestDetailsFromUnit } from "../utils/apiUtils";
import { BookingAvailability } from "../../../api-contracts/sites/wbe/booking-availability";
import {
  getStopTimeMessageForStayRange,
  isStayRangeBlockedByStopTime,
} from "../utils/bookingAvailabilityUtils";
import { getDateFnsLocale, getLanguageCode } from "../utils/language";
import { FiChevronRight, FiPlus } from "react-icons/fi";
import { toasty } from "./Toast";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SearchUnit } from "../contexts/HeaderContext";
import { useRoomInfoSelectionOrNull } from "../contexts/RoomInfoSelectionContext";
import { useRoomInfoPricing } from "../pages/landing/hooks/useRoomInfoPricing";
import { buildPackageAvailability } from "../pages/landing/packageAvailability";
import { usePackagePreview } from "../pages/landing/packagePreviewMode";
import { previewVariable } from "../pages/landing/packagePreview";
import {
  useGetPackageDetail,
  useGetPackageDetailsByUnits,
} from "../api/api-hooks/package-ads";
import { GuestDetails } from "../api/contracts/reservation";
import {
  UnitGuestCounts,
  areAllUnitsChosen,
  getNextUnitNeedingChoice,
  shouldAutoSkipCategoryChoice,
  sortCategoriesByAvailability,
} from "../pages/landing/packageDetailLogic";
import { PackageCategoryChoice } from "../pages/landing/components/PackageCategoryChoice";
import { countChoicesExcluding } from "../pages/landing/packageDetailLogic";
import { PackageRoomRow } from "../pages/landing/components/PackageRoomRow";
import { resizeGuestList } from "../utils/resizeGuestList";
import { isInfantsOnlyGuestList } from "../utils/guestCombination";
import { useBookingCodeStore } from "../stores/bookingCodeStore";
import { useBookingCodeCommit } from "./useBookingCodeCommit";
import { MobileBookingCodeBlock } from "./MobileBookingCodeBlock";
import { useAdFormat } from "../hooks/useAdFormat";
import { useSelectAdFormat } from "../hooks/useSelectAdFormat";
import { bookedUnitIds, useBookingFlowStore, unitCapReached } from "../booking-flow/bookingFlowStore";
import { AdFormat } from "../api/contracts/site-config";

interface GuestLimits {
  adult: { min: number; max: number };
  teenager: { min: number; max: number };
  child: { min: number; max: number };
  infant: { min: number; max: number };
}

interface MobileSearchSheetProps {
  isOpen: boolean;
  onClose: () => void;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  adultCount: number;
  teenagerCount: number;
  childCount: number;
  infantCount: number;
  childGuests: Guest[];
  infantGuests: Guest[];
  onDateSelect: (date: Date) => void;
  onRangeChange?: (from: Date, to: Date | null) => void;
  onAdultsChange: (count: number) => void;
  onTeenagersChange: (count: number) => void;
  onChildrenChange: (count: number) => void;
  onInfantsChange: (count: number) => void;
  onChildAgeChange: (childId: number, age: number) => void;
  onInfantAgeChange: (infantId: number, age: number) => void;
  onSearch?: () => void;
  guestLimits: GuestLimits;
  totalGuests: number;
  maxTotalGuests: number;
  guestTypesConfig?: GuestTypesConfig;
  bookingAvailability?: BookingAvailability;
  /** Site-wide availability request in flight (plain search mode's feed). */
  bookingAvailabilityFetching?: boolean;
  mode?: "search" | "roomInfo" | "package";
  categoryAdId?: string;
  /** Package mode: bound category-ad ids to union for availability, plus the
   *  package ad's arrival window. Mirrors the desktop PackageDetailDialog
   *  calendar so out-of-window arrivals are blocked on mobile too. */
  packageAdId?: string;
  packageCategoryAdIds?: string[];
  packageArrivalDateFrom?: string | null;
  packageArrivalDateTo?: string | null;
  packageStayPeriodFrom?: string | null;
  packageStayPeriodTo?: string | null;
  /** Which section to open on. Omitted opens on dates, which is what the header
   *  and the date row want. The package dialog's "choose a category" CTA sends
   *  the guest straight to that step instead. */
  openOnStep?: "dates" | "guests" | "category";
}

export const MobileSearchSheet: React.FC<MobileSearchSheetProps> = ({
  isOpen,
  onClose,
  selectedStartDate: selectedStartDateProp,
  selectedEndDate: selectedEndDateProp,
  adultCount: adultCountProp,
  teenagerCount: teenagerCountProp,
  childCount: childCountProp,
  infantCount: infantCountProp,
  childGuests: childGuestsProp,
  infantGuests: infantGuestsProp,
  onDateSelect: onDateSelectProp,
  onRangeChange: onRangeChangeProp,
  onAdultsChange: onAdultsChangeProp,
  onTeenagersChange: onTeenagersChangeProp,
  onChildrenChange: onChildrenChangeProp,
  onInfantsChange: onInfantsChangeProp,
  onChildAgeChange: onChildAgeChangeProp,
  onInfantAgeChange: onInfantAgeChangeProp,
  onSearch,
  guestLimits,
  totalGuests: totalGuestsProp,
  maxTotalGuests,
  guestTypesConfig,
  bookingAvailability,
  bookingAvailabilityFetching = false,
  mode = "search",
  categoryAdId,
  packageAdId,
  packageCategoryAdIds,
  packageArrivalDateFrom,
  packageArrivalDateTo,
  packageStayPeriodFrom,
  packageStayPeriodTo,
  openOnStep,
}) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  // "Lägg till boende"/"Lägg till grupp" collapses whatever unit section is
  // open and expands the new one in the same tap — two height changes plus a
  // growing list, all in one frame. No easing reads as deliberate there, so
  // that one transition is instant instead. The flag flips on via flushSync,
  // forcing its own commit so the still-open unit's motion.div picks up
  // duration:0 one render BEFORE it exits (AnimatePresence animates an
  // exiting element from its last-rendered props, not the render where it
  // disappears) — the add itself then runs in the following (batched) commit,
  // and a render later the flag resets so the next header tap animates again.
  // Shared by both accordion copies below (room-info mode and search mode
  // never render at the same time).
  const [isInstantUnitChange, setIsInstantUnitChange] = useState(false);
  useEffect(() => {
    if (isInstantUnitChange) setIsInstantUnitChange(false);
  }, [isInstantUnitChange]);
  const skipAccordionTransition = reduceMotion || isInstantUnitChange;
  // Read off the URL rather than taken as a prop: the sheet's parent is Header,
  // which knows nothing about packages, and the param is already there.
  const isPackagePreviewMode = usePackagePreview(packageAdId);
  const calendarLocalization = useCalendarLabels();
  const guestSelectLabels = useGuestSelectLabels();
  const {
    selectedLanguage,
    hasTodayArrivalDeeplink,
    units,
    activeUnitId,
    setActiveUnitId,
    getBedTypeForAge,
    setSelectedStartDate,
    setSelectedEndDate,
    replaceUnits,
  } = useHeader();
  const { configuration } = useConfiguration();
  const { format: committedFormat } = useAdFormat();
  const selectFormat = useSelectAdFormat();
  const siteId = configuration?.siteId || "";

  const roomInfoCtx = useRoomInfoSelectionOrNull();
  const isRoomInfoMode = !!categoryAdId || mode === "roomInfo";
  const isPackageMode = mode === "package";
  // roomInfo and package are both opened from a dialog backed by
  // RoomInfoSelectionContext: they read/commit dates through it, share its
  // calendar month, and buffer edits as drafts. Only the availability feed
  // differs (single category vs bound-category union + package arrival window),
  // so category-specific paths stay gated on `useCtxRoomInfo`.
  const isDialogMode = isRoomInfoMode || isPackageMode;
  const useCtxRoomInfo = isRoomInfoMode && !!roomInfoCtx;
  const useCtxDialog = isDialogMode && !!roomInfoCtx;
  const isSearchMode = !isDialogMode;

  // Draft state for search mode — edits inside the sheet write here instead of
  // straight to HeaderContext, so closing without tapping "Uppdatera" discards
  // them. Matches HeaderSearchBar's desktop draft pattern. roomInfo mode keeps
  // its own context-based drafts via RoomInfoSelectionContext.
  const [draftStartDate, setDraftStartDate] = useState<Date | null>(
    selectedStartDateProp,
  );
  const [draftEndDate, setDraftEndDate] = useState<Date | null>(
    selectedEndDateProp,
  );
  // The booking flow owns the plan's units. A unit whose rows are in the cart is a booking, not
  // a search draft — it is switched and dropped in the flow, never here. The sheet then edits
  // the units still to book, and a search puts those back beside the booked ones untouched.
  // See `HeaderSearchBar` for the same.
  const plan = useBookingFlowStore((s) => s.plan);
  const booked = useMemo(() => bookedUnitIds(plan), [plan]);
  const draftFor = useCallback(
    (all: SearchUnit[], active: string): SearchUnit[] => {
      const single = (list: SearchUnit[]) =>
        committedFormat === "activity" ? keepActiveUnit(list, active) : list;
      const open = all.filter((u) => !booked.has(u.id));
      if (open.length > 0) return single(open);
      const seed = all.find((u) => u.id === active) ?? all[0];
      return [
        {
          id: createUnitId(),
          guests: seed
            ? seed.guests.map((g) => ({ ...g }))
            : [{ ageType: "adult", bedCapacityType: "full" } as Guest],
        },
      ];
    },
    [booked, committedFormat],
  );
  // The header's active unit may be a booked one, absent from the draft — then the first
  // unit still to book is the one open in the sheet.
  const activeIn = (draft: SearchUnit[], active: string): string =>
    draft.some((u) => u.id === active) ? active : (draft[0]?.id ?? active);
  const [draftUnits, setDraftUnits] = useState<SearchUnit[]>(() =>
    draftFor(units, activeUnitId),
  );
  const [draftActiveUnitId, setDraftActiveUnitId] = useState<string>(() =>
    activeIn(draftUnits, activeUnitId),
  );
  // Format is staged like the rest: tapping a tab must not swap the landing
  // page's content while the guest is still filling the form.
  const [draftFormat, setDraftFormat] = useState<AdFormat | undefined>(
    committedFormat,
  );

  // Activity sites book groups, not lodging units — same model, other words.
  // The sheet answers to the draft so the calendar and guest rows re-shape as
  // soon as a format is picked, before anything is committed.
  const isActivitySite =
    (isSearchMode ? (draftFormat ?? committedFormat) : committedFormat) ===
    "activity";
  // An activity availability search prices ONE guest mix — no add-group. The cap counts the
  // booked units too, and the sheet only holds the ones still to book.
  const unitCap = configuration?.maxConfig?.maxUnits;
  const maxUnits = isActivitySite
    ? 1
    : typeof unitCap === "number"
      ? Math.max(1, unitCap - booked.size)
      : unitCap;

  // Booking code — staged like dates/guests and applied on the unified
  // "Uppdatera" commit (search mode only). Unlike the other drafts here, this
  // one lives in the store: the desktop segment renders the same field, and
  // crossing the breakpoint unmounts one surface in favour of the other. Note
  // the consequence — an abandoned edit is NOT snapped back on reopen the way
  // dates and guests are, because "abandoned here" and "typed on the other
  // surface" are the same state.
  const draftBookingCode = useBookingCodeStore((state) => state.draft);
  const setDraftBookingCode = useBookingCodeStore((state) => state.setDraft);
  const commitBookingCode = useBookingCodeCommit(siteId);
  // Shown in BOTH search and room-info ("Se tillgänglighet") modes — applying
  // a code re-prices either flow. Gated only on the site opting in.
  const showBookingCodeBlock = configuration?.acceptBookingCodes === true;

  const [step, setStep] = useState<"dates" | "guests" | "category">("dates");
  const categorySectionRef = useRef<HTMLDivElement | null>(null);
  const [searchCurrentMonth, setSearchCurrentMonth] = useState(new Date());
  // Calendar month is shared with RoomInfoDialog via context in roomInfo mode,
  // so scrolling on one view follows through to the other when the viewport
  // changes mid-selection.
  const currentMonth = useCtxDialog
    ? roomInfoCtx.currentMonth
    : searchCurrentMonth;
  const setCurrentMonth = useCtxDialog
    ? roomInfoCtx.setCurrentMonth
    : setSearchCurrentMonth;

  // Active draft unit. Drafts are stored as full SearchUnit[] and edits go
  // through the active unit id; both search and roomInfo modes share this.
  const draftActiveUnit =
    draftUnits.find((u) => u.id === draftActiveUnitId) ?? draftUnits[0];
  const draftActiveGuests = draftActiveUnit?.guests ?? [];
  const draftAdultCount = draftActiveGuests.filter(
    (g) => g.ageType === "adult",
  ).length;
  const draftTeenagerCount = draftActiveGuests.filter(
    (g) => g.ageType === "teenager",
  ).length;
  const draftChildGuests = draftActiveGuests.filter(
    (g) => g.ageType === "child",
  );
  const draftInfantGuests = draftActiveGuests.filter(
    (g) => g.ageType === "infant",
  );

  // Both search and roomInfo modes read from drafts so context is only
  // touched on explicit commit (Uppdatera). The fall-through case (roomInfo
  // mode but no provider) keeps the prop-based pass-through for safety.
  const useDrafts = isSearchMode || useCtxDialog;
  const selectedStartDate = useDrafts ? draftStartDate : selectedStartDateProp;
  const selectedEndDate = useDrafts ? draftEndDate : selectedEndDateProp;
  const childGuests = useDrafts ? draftChildGuests : childGuestsProp;
  const infantGuests = useDrafts ? draftInfantGuests : infantGuestsProp;
  const adultCount = useDrafts ? draftAdultCount : adultCountProp;
  const teenagerCount = useDrafts ? draftTeenagerCount : teenagerCountProp;
  const childCount = useDrafts ? draftChildGuests.length : childCountProp;
  const infantCount = useDrafts ? draftInfantGuests.length : infantCountProp;
  const totalGuests = useDrafts ? draftActiveGuests.length : totalGuestsProp;

  const effectiveUnits = useDrafts ? draftUnits : units;
  const effectiveActiveUnitId = useDrafts ? draftActiveUnitId : activeUnitId;

  // Totals across every unit — the sheet's own collapsed "Guests" summary
  // (shown next to the section heading whether or not it's expanded)
  // describes the whole party, not just the active/last-expanded unit. The
  // per-unit adultCount/teenagerCount/etc. above stay scoped to the active
  // unit — that's correct for the open accordion section's own steppers.
  const guestTypeTotals = useMemo(
    () => sumGuestTypesAcrossUnits(effectiveUnits),
    [effectiveUnits],
  );

  // --- Draft writers (shared by search mode and roomInfo mode) ---
  const handleSearchDateSelect = useCallback(
    (date: Date) => {
      if (draftStartDate && isSameDay(date, draftStartDate) && !draftEndDate) {
        setDraftStartDate(null);
        return;
      }
      if (!draftStartDate || (draftStartDate && draftEndDate)) {
        setDraftStartDate(date);
        setDraftEndDate(null);
        return;
      }
      if (date < draftStartDate) {
        setDraftStartDate(date);
        setDraftEndDate(draftStartDate);
        return;
      }
      setDraftEndDate(date);
    },
    [draftStartDate, draftEndDate],
  );

  const handleSearchRangeChange = useCallback((from: Date, to: Date | null) => {
    setDraftStartDate(from);
    setDraftEndDate(to);
  }, []);

  const setSearchDraftGuestCount = useCallback(
    (ageType: AgeType, count: number) => {
      if (!configuration) return;
      setDraftUnits((prev) =>
        prev.map((unit) => {
          if (unit.id !== draftActiveUnitId) return unit;
          return {
            ...unit,
            guests: resizeGuestList(unit.guests, ageType, count, configuration),
          };
        }),
      );
    },
    [configuration, draftActiveUnitId],
  );

  const updateSearchDraftGuest = useCallback(
    (
      ageType: "child" | "infant",
      indexWithinType: number,
      updates: Partial<Guest>,
    ) => {
      setDraftUnits((prev) =>
        prev.map((unit) => {
          if (unit.id !== draftActiveUnitId) return unit;
          let seen = 0;
          const guests = unit.guests.map((guest) => {
            if (guest.ageType !== ageType) return guest;
            const match = seen === indexWithinType;
            seen += 1;
            return match ? ({ ...guest, ...updates } as Guest) : guest;
          });
          return { ...unit, guests };
        }),
      );
    },
    [draftActiveUnitId],
  );

  const handleSearchAddUnit = useCallback(() => {
    if (typeof maxUnits === "number" && draftUnits.length >= maxUnits) return;
    const newId = createUnitId();
    setDraftUnits((prev) => {
      const source =
        prev.find((u) => u.id === draftActiveUnitId) ?? prev[prev.length - 1];
      const seededGuests = source
        ? source.guests.map((g) => ({ ...g }))
        : [{ ageType: "adult", bedCapacityType: "full" } as Guest];
      return [...prev, { id: newId, guests: seededGuests }];
    });
    setDraftActiveUnitId(newId);
  }, [maxUnits, draftUnits.length, draftActiveUnitId]);

  /** Open a unit, or close the one already open. Setting the active id to the
   *  unit that was already active left the accordion stuck open — with several
   *  units that means the list can only ever grow, never fold back to a
   *  scannable set of headers. The empty id is the closed state: it matches no
   *  unit, so the guest edit handlers below simply have nothing to write to. */
  const toggleActiveUnit = useCallback(
    (unitId: string) =>
      setDraftActiveUnitId((prev) => (prev === unitId ? "" : unitId)),
    [],
  );

  const handleSearchRemoveUnit = useCallback(
    (unitId: string) => {
      setDraftUnits((prev) => {
        if (prev.length <= 1) return prev;
        const next = prev.filter((u) => u.id !== unitId);
        // Only re-target when the OPEN unit was the one removed — an accordion
        // the guest deliberately closed must not spring open again.
        if (
          draftActiveUnitId &&
          !next.some((u) => u.id === draftActiveUnitId) &&
          next[0]
        ) {
          setDraftActiveUnitId(next[0].id);
        }
        return next;
      });
    },
    [draftActiveUnitId],
  );

  // Effective handlers (route to ctx in roomInfo mode, drafts in search mode).
  const onDateSelect = useDrafts ? handleSearchDateSelect : onDateSelectProp;
  const onRangeChange = useDrafts ? handleSearchRangeChange : onRangeChangeProp;
  const onAdultsChange = useDrafts
    ? (count: number) => setSearchDraftGuestCount("adult", count)
    : onAdultsChangeProp;
  const onTeenagersChange = useDrafts
    ? (count: number) => setSearchDraftGuestCount("teenager", count)
    : onTeenagersChangeProp;
  const onChildrenChange = useDrafts
    ? (count: number) => setSearchDraftGuestCount("child", count)
    : onChildrenChangeProp;
  const onInfantsChange = useDrafts
    ? (count: number) => setSearchDraftGuestCount("infant", count)
    : onInfantsChangeProp;
  const onChildAgeChange = useDrafts
    ? (idx: number, age: number) =>
        updateSearchDraftGuest("child", idx, {
          age,
          bedCapacityType: getBedTypeForAge(age),
        })
    : onChildAgeChangeProp;
  const onInfantAgeChange = useDrafts
    ? (idx: number, age: number) =>
        updateSearchDraftGuest("infant", idx, {
          age,
          bedCapacityType: getBedTypeForAge(age),
        })
    : onInfantAgeChangeProp;

  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const openedNow = isOpen && !wasOpenRef.current;
    if (openedNow) {
      const anchorDate = selectedStartDate ?? selectedEndDate;
      if (anchorDate) setCurrentMonth(anchorDate);
      // Snapshot context into drafts on every open so any external commit
      // since last open is reflected, and any uncommitted edit from a prior
      // open is discarded. Both modes use the same drafts; the source
      // differs (HeaderContext for search, RoomInfoSelectionContext for
      // roomInfo/package).
      if (useCtxDialog && roomInfoCtx) {
        setDraftStartDate(roomInfoCtx.startDate);
        setDraftEndDate(roomInfoCtx.endDate);
        setDraftUnits(roomInfoCtx.units);
        setDraftActiveUnitId(roomInfoCtx.activeUnitId);
      } else if (isSearchMode) {
        setDraftStartDate(selectedStartDateProp);
        setDraftEndDate(selectedEndDateProp);
        const next = draftFor(units, activeUnitId);
        setDraftUnits(next);
        setDraftActiveUnitId(activeIn(next, activeUnitId));
        setDraftFormat(committedFormat);
        // The booking-code draft is deliberately not reset here: it is shared
        // with the desktop segment, so resetting it would discard a code typed
        // before the viewport crossed the breakpoint.
      }
      if (openOnStep) {
        setStep(openOnStep);
        // The section sits below dates and guests, so expanding it is not
        // enough — on a phone it opens off-screen. Deferred past the sheet's
        // own entrance transition, which is what moves it into place.
        setTimeout(
          () =>
            categorySectionRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            }),
          320,
        );
      } else {
        setStep("dates");
      }
    }
    wasOpenRef.current = isOpen;
    // Intentionally narrow deps: the body only runs on the false→true
    // transition (gated by openedNow), so all reads inside take their
    // values from the render that contains that transition. Adding the
    // remaining reads would just churn the effect without changing
    // observable behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedStartDate, selectedEndDate]);

  const formatDateRange = () => {
    if (!selectedStartDate || !selectedEndDate) return "";
    const startLabel = format(selectedStartDate, "d MMM", { locale: getDateFnsLocale() });
    const endLabel = format(selectedEndDate, "d MMM", { locale: getDateFnsLocale() });
    // Activity searches bound days, not stays — a single day shows as itself
    // and a range shows without the nights count.
    if (isActivitySite) {
      return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
    }
    // Calendar nights, not ceiled 24-hour periods — a DST night is 23 or 25 hours.
    const nights = differenceInCalendarDays(selectedEndDate, selectedStartDate);
    return `${startLabel} - ${endLabel} (${nights} ${t("common.nights")})`;
  };

  const shouldPromptDepartureAndGuests =
    !!selectedStartDate && !selectedEndDate && hasTodayArrivalDeeplink;

  const formatGuestInfo = () => {
    if (!isActivitySite && effectiveUnits.length > 1) {
      return t("header.units_short", { count: effectiveUnits.length });
    }
    return formatGuests(
      guestTypeTotals.adult,
      guestTypeTotals.teenager,
      guestTypeTotals.child,
      guestTypeTotals.infant,
      t,
    );
  };

  const { children: transformedChildGuests, infants: transformedInfantGuests } =
    transformGuestsForSearch(childGuests, infantGuests);

  const searchVariables: SearchCategoryAdsRequest & {
    languageCode: string;
  } = {
    languageCode: selectedLanguage,
    arrivalDate: selectedStartDate ? formatDateForAPI(selectedStartDate) : "",
    departureDate: selectedEndDate ? formatDateForAPI(selectedEndDate) : "",
    guests: {
      adults: adultCount,
      teenagers: teenagerCount,
      children: transformedChildGuests,
      infants: transformedInfantGuests,
    },
  };

  const { refetch } = useGetCategories({
    variables: { ...searchVariables, siteId, filter: "active" },
    enabled: false,
  });

  const availabilityWindow = useMemo(() => {
    if (!isDialogMode) return null;
    // The Calendar renders a full week grid: leading days from the previous
    // month and trailing days from the next month appear as padding. Those
    // padding days must be fetched too, otherwise they look bookable while
    // the user picks arrival. Clamp from-date to today since the backend
    // rejects queryFromDate < today.
    const today = startOfDay(new Date());
    let from = maxDate([
      startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 }),
      today,
    ]);
    if (selectedStartDate) {
      const arrivalMonthFrom = maxDate([
        startOfWeek(startOfMonth(selectedStartDate), { weekStartsOn: 1 }),
        today,
      ]);
      if (isBefore(arrivalMonthFrom, from)) from = arrivalMonthFrom;
    }
    let rawTo = endOfWeek(endOfMonth(addMonths(currentMonth, 1)), {
      weekStartsOn: 1,
    });
    // Extend `to` to cover the departure month when one is picked, so the
    // bottleneck calculation in `remainingCategoryUnits` sees every stayed-on
    // night. Without this a stay past the visible grid leaves nights
    // missing from `dates` → treated as 0 vacancy → blocks add-unit.
    if (selectedEndDate) {
      const departureMonthTo = endOfWeek(endOfMonth(selectedEndDate), {
        weekStartsOn: 1,
      });
      if (isBefore(rawTo, departureMonthTo)) rawTo = departureMonthTo;
    }
    if (isBefore(rawTo, from)) return null;
    return {
      queryFromDate: format(from, "yyyy-MM-dd"),
      queryToDate: format(rawTo, "yyyy-MM-dd"),
    };
  }, [isDialogMode, currentMonth, selectedStartDate, selectedEndDate]);

  const roomInfoGuestsForAvailability = useMemo(() => {
    const totals = { adults: 0, teenagers: 0, children: 0, infants: 0 };
    const neutralFallback = {
      adults: 0,
      teenagers: 0,
      children: 0,
      infants: 0,
    };
    effectiveUnits.forEach((unit) => {
      unit.guests.forEach((guest) => {
        if (guest.ageType === "adult") totals.adults += 1;
        else if (guest.ageType === "teenager") totals.teenagers += 1;
        else if (guest.ageType === "child") totals.children += 1;
        else if (guest.ageType === "infant") totals.infants += 1;
      });
    });
    if (
      totals.adults + totals.teenagers + totals.children + totals.infants >
      0
    ) {
      return totals;
    }
    if (isDialogMode) {
      // Keep dialog-mode (roomInfo/package) probe state isolated from header
      // guest counters.
      return neutralFallback;
    }
    const fallbackFromCounters = {
      adults: adultCount,
      teenagers: teenagerCount,
      children: childGuests.length,
      infants: infantGuests.length,
    };
    return fallbackFromCounters;
  }, [
    effectiveUnits,
    isDialogMode,
    adultCount,
    teenagerCount,
    childGuests.length,
    infantGuests.length,
  ]);

  // Mirror RoomInfoDialog's price probe so the confirm button waits on
  // pricing too. No-ops without categoryAdId, so plain search mode pays nothing.
  const { isPricing } = useRoomInfoPricing({
    siteId,
    selectedLanguage,
    categoryId: categoryAdId,
    selectedStartDate,
    selectedEndDate,
    units: effectiveUnits.map((unit) => ({ id: unit.id, guests: unit.guests })),
  });

  const {
    data: categoryCalendarData,
    isFetching: isFetchingCategoryCalendar,
    isPlaceholderData: isCategoryCalendarPlaceholder,
  } = useGetAvailabilityCalendar({
    variables: {
      siteId,
      queryFromDate: availabilityWindow?.queryFromDate ?? "",
      queryToDate: availabilityWindow?.queryToDate ?? "",
      guests: roomInfoGuestsForAvailability,
      adIds: categoryAdId ? [categoryAdId] : undefined,
    },
    enabled:
      !!siteId && !!categoryAdId && !!availabilityWindow && isRoomInfoMode,
    // Match desktop room-info behavior: always refetch when this sheet mounts
    // so fast back-navigation cannot reuse stale pre-cart availability.
    refetchOnMount: "always",
    // Keep previous month's data while refetching for a newly navigated
    // month — otherwise every visible date is flagged unavailable during
    // the request and cross-month departure picking breaks.
    placeholderData: keepPreviousData,
  });

  const categoryBookingAvailability = useMemo<
    BookingAvailability | undefined
  >(() => {
    if (!isRoomInfoMode || !categoryAdId) return undefined;
    // Cover the same week-aligned range the Calendar component renders so
    // leading/trailing padding days are flagged unavailable too.
    const fromDt = startOfWeek(startOfMonth(currentMonth), {
      weekStartsOn: 1,
    });
    const toDt = endOfWeek(endOfMonth(addMonths(currentMonth, 1)), {
      weekStartsOn: 1,
    });
    const adEntry = categoryCalendarData?.find((ad) => ad.id === categoryAdId);
    // Keep parity with RoomInfoDialog desktop calendar: treat a date as
    // available only when it can satisfy the whole staged selection.
    // In room-info mobile sheet this includes all staged units.
    const requiredUnits = Math.max(
      1,
      isRoomInfoMode ? effectiveUnits.length : 1,
    );
    // Per-night arrival filter: a date is bookable as an arrival when this
    // single night can satisfy the staged units. Stay-window validity (every
    // night up to departure has enough vacancy) is handled by the Calendar's
    // departure-picker against bookableNightDates below.
    const sufficientDateSet = categoryCalendarData
      ? new Set(
          (adEntry?.dates ?? [])
            .filter((d) => d.effectiveAvailability >= requiredUnits)
            .map((d) => d.date),
        )
      : new Set<string>();
    // Category ad's arrival window — dates outside count as arrival-only
    // restricted (red while picking arrival, selectable as departure since
    // their nights remain bookable as middle-stay nights).
    const arrivalFromKey = adEntry?.arrivalDateFrom ?? null;
    const arrivalToKey = adEntry?.arrivalDateTo ?? null;
    const hasArrivalWindow = !!arrivalFromKey || !!arrivalToKey;
    const unavailableDates: string[] = [];
    const arrivalOnlyRestrictedDates: string[] = [];
    for (let d = fromDt; !isBefore(toDt, d); d = addDays(d, 1)) {
      const key = format(d, "yyyy-MM-dd");
      if (!sufficientDateSet.has(key)) {
        unavailableDates.push(key);
        continue;
      }
      if (
        hasArrivalWindow &&
        ((arrivalFromKey && key < arrivalFromKey) ||
          (arrivalToKey && key > arrivalToKey))
      ) {
        arrivalOnlyRestrictedDates.push(key);
      }
    }
    // Single-category mobile flow: expose this ad's bookable nights and
    // arrival-allowed dates so departure picking is bounded by THIS ad's
    // coverage rather than site-wide. The ad id stands in for the category
    // key — the per-category logic only treats it as an identity.
    const bookableNightDates = [...sufficientDateSet];
    const arrivalOnlyRestrictedSet = new Set(arrivalOnlyRestrictedDates);
    const arrivalAllowedDates = bookableNightDates.filter(
      (d) => !arrivalOnlyRestrictedSet.has(d),
    );
    return {
      unavailableDates,
      arrivalOnlyRestrictedDates,
      categories: categoryAdId
        ? [
            {
              categoryId: categoryAdId,
              bookableNightDates,
              arrivalAllowedDates,
            },
          ]
        : [],
      stopTimes: bookingAvailability?.stopTimes ?? [],
    };
  }, [
    isRoomInfoMode,
    categoryAdId,
    categoryCalendarData,
    currentMonth,
    bookingAvailability,
    effectiveUnits.length,
  ]);

  // Package mode: union availability across the bound category ads plus the
  // package ad's arrival window — the exact feed the desktop PackageDetailDialog
  // calendar uses, so mobile blocks out-of-window arrivals identically.
  const {
    data: packageCalendarData,
    isFetching: isFetchingPackageCalendar,
    isPlaceholderData: isPackageCalendarPlaceholder,
  } = useGetAvailabilityCalendar({
    variables: {
      siteId,
      queryFromDate: availabilityWindow?.queryFromDate ?? "",
      queryToDate: availabilityWindow?.queryToDate ?? "",
      guests: roomInfoGuestsForAvailability,
      // Scope by package ad so the server resolves the bindings itself and
      // includes `package_only` categories, which an adIds-scoped query hides.
      packageAdId: isPackageMode ? packageAdId : undefined,
      // A previewed ad unlocks no bindings without this, leaving the sheet with
      // an empty calendar where the desktop dialog shows dates.
      preview: previewVariable(isPackagePreviewMode),
    },
    enabled:
      !!siteId &&
      isPackageMode &&
      !!availabilityWindow &&
      !!packageAdId &&
      !!packageCategoryAdIds?.length,
    refetchOnMount: "always",
    placeholderData: keepPreviousData,
  });

  const packageBookingAvailability = useMemo<
    BookingAvailability | undefined
  >(() => {
    if (
      !isPackageMode ||
      !packageCategoryAdIds?.length ||
      !packageCalendarData
    ) {
      return undefined;
    }
    // Cover the same week-aligned range the Calendar renders (leading/trailing
    // padding days included), matching the category path above.
    const windowFrom = startOfWeek(startOfMonth(currentMonth), {
      weekStartsOn: 1,
    });
    const windowTo = endOfWeek(endOfMonth(addMonths(currentMonth, 1)), {
      weekStartsOn: 1,
    });
    return buildPackageAvailability({
      calendarData: packageCalendarData,
      boundCategories: packageCategoryAdIds.map((id) => ({
        categoryAdId: id,
        categoryId: id,
      })),
      requiredUnits: Math.max(1, effectiveUnits.length),
      windowFrom,
      windowTo,
      packageArrivalFrom: packageArrivalDateFrom ?? null,
      packageArrivalTo: packageArrivalDateTo ?? null,
      stayPeriodFrom: packageStayPeriodFrom ?? null,
      stayPeriodTo: packageStayPeriodTo ?? null,
      stopTimes: bookingAvailability?.stopTimes ?? [],
    });
  }, [
    isPackageMode,
    packageCategoryAdIds,
    packageCalendarData,
    currentMonth,
    effectiveUnits.length,
    packageArrivalDateFrom,
    packageArrivalDateTo,
    packageStayPeriodFrom,
    packageStayPeriodTo,
    bookingAvailability,
  ]);

  const activeBookingAvailability = isRoomInfoMode
    ? categoryBookingAvailability
    : isPackageMode
      ? packageBookingAvailability
      : bookingAvailability;

  // Read the loading state off whichever feed this mode is showing.
  const {
    isStale: isAvailabilityStale,
    isRefreshing: isAvailabilityRefreshing,
  } = isRoomInfoMode
    ? getAvailabilityLoadingState({
        isFetching: isFetchingCategoryCalendar,
        isPlaceholderData: isCategoryCalendarPlaceholder,
        hasData: !!categoryCalendarData,
      })
    : isPackageMode
      ? getAvailabilityLoadingState({
          isFetching: isFetchingPackageCalendar,
          isPlaceholderData: isPackageCalendarPlaceholder,
          hasData: !!packageCalendarData,
        })
      : getAvailabilityLoadingState({
          isFetching: bookingAvailabilityFetching,
          hasData: !!bookingAvailability,
        });
  // Guest steppers, add/remove unit and the footer CTA all act on availability
  // or price, so they wait for both probes. Room-info mode is the only one
  // that prices per guest mix in here; the others have no `isPricing`.
  const isSelectionPending =
    isAvailabilityStale ||
    isAvailabilityRefreshing ||
    (isRoomInfoMode && isPricing);

  // Cart rows count toward the per-reservation max-units cap. Same pattern
  // as RoomInfoDialog so the sheet's "add unit" button stays in sync with
  // the dialog's footer add button.
  const reservationKey = useBookingStore((s) => s.reservationKey);
  const { data: cartReservation } = useGetReservation({
    variables: {
      reservationKey: reservationKey || "",
      languageCode: selectedLanguage,
      siteId,
    },
    enabled: !!reservationKey && !!siteId && useCtxRoomInfo,
  });
  const cartRowsTotal = cartReservation?.rows?.length ?? 0;

  const canAddRoomInfoUnitLocal = useMemo(
    () =>
      typeof maxUnits !== "number" ||
      cartRowsTotal + effectiveUnits.length < maxUnits,
    [maxUnits, cartRowsTotal, effectiveUnits.length],
  );

  const handleAddRoomInfoUnit = useCallback(() => {
    if (!canAddRoomInfoUnitLocal) return;
    const newUnitId = createUnitId();
    setDraftUnits((prev) => {
      const sourceUnit =
        prev.find((u) => u.id === draftActiveUnitId) ?? prev[prev.length - 1];
      const newUnit: SearchUnit = {
        id: newUnitId,
        guests: sourceUnit
          ? sourceUnit.guests.map((g) => ({ ...g }))
          : [{ ageType: "adult", bedCapacityType: "full" }],
      };
      return [...prev, newUnit];
    });
    setDraftActiveUnitId(newUnitId);
  }, [canAddRoomInfoUnitLocal, draftActiveUnitId]);

  const handleRemoveRoomInfoUnit = useCallback(
    (unitId: string) => {
      setDraftUnits((prev) => {
        if (prev.length <= 1) return prev;
        const next = prev.filter((u) => u.id !== unitId);
        if (!next.some((u) => u.id === draftActiveUnitId) && next[0]) {
          setDraftActiveUnitId(next[0].id);
        }
        return next;
      });
    },
    [draftActiveUnitId],
  );

  const remainingCategoryUnits = useMemo<number | undefined>(() => {
    if (!isRoomInfoMode || !categoryAdId) return undefined;
    if (!selectedStartDate || !selectedEndDate) return undefined;
    const adEntry = categoryCalendarData?.find((ad) => ad.id === categoryAdId);
    if (!adEntry) return 0;
    // Bottleneck across the picked stay window: minimum per-night vacancy
    // between arrival (inclusive) and departure (exclusive). Frontend owns
    // this calculation now that backend emits raw per-night availability.
    const byDate = new Map(
      adEntry.dates.map((d) => [d.date, d.effectiveAvailability]),
    );
    let min = Infinity;
    for (
      let d = selectedStartDate;
      isBefore(d, selectedEndDate);
      d = addDays(d, 1)
    ) {
      const key = format(d, "yyyy-MM-dd");
      const v = byDate.get(key) ?? 0;
      if (v < min) min = v;
    }
    return Number.isFinite(min) ? min : 0;
  }, [
    isRoomInfoMode,
    categoryAdId,
    selectedStartDate,
    selectedEndDate,
    categoryCalendarData,
  ]);

  const canAddRoomInfoUnitWithAvailability =
    canAddRoomInfoUnitLocal &&
    // Mirror desktop behavior: block "add unit" while only arrival is picked.
    !(selectedStartDate && !selectedEndDate) &&
    (remainingCategoryUnits === undefined ||
      effectiveUnits.length < remainingCategoryUnits);

  // Sum of staged guests across every unit. Used to gate the bottom confirm
  // button so an empty booking can't be committed from the sheet.
  const totalGuestsAcrossUnits = useMemo(
    () => effectiveUnits.reduce((sum, unit) => sum + unit.guests.length, 0),
    [effectiveUnits],
  );

  // Infants-only is never bookable — an infant cannot occupy a unit alone. Any
  // unit composed only of infants blocks search/commit from this sheet.
  const hasInfantsOnlyUnit = useMemo(
    () => effectiveUnits.some((unit) => isInfantsOnlyGuestList(unit.guests ?? [])),
    [effectiveUnits],
  );

  // --- Package mode: category choice as the sheet's third step ---
  // The priced detail is keyed on the COMMITTED selection, which is why moving
  // on from the guest step commits the drafts first: the prices, availability
  // and guest fit shown on the cards must describe the stay the guest just
  // picked, not the one they opened the sheet with. Same query key as Landing's
  // detail fetch, so this shares that cache rather than adding a round-trip.
  const committedUnits = useMemo(
    () => roomInfoCtx?.units ?? [],
    [roomInfoCtx?.units],
  );

  const packageDetailGuests = useMemo<GuestDetails>(() => {
    const allGuests = committedUnits.flatMap((u) => u.guests ?? []);
    const { children: childList, infants: infantList } =
      splitChildAndInfantGuests(allGuests);
    const { children, infants } = transformGuestsForSearch(
      childList,
      infantList,
    );
    return {
      adults: allGuests.filter((g) => g.ageType === "adult").length,
      teenagers: allGuests.filter((g) => g.ageType === "teenager").length,
      children,
      infants,
    };
  }, [committedUnits]);

  const draftPackageDetailGuests = useMemo<GuestDetails>(() => {
    const allGuests = draftUnits.flatMap((u) => u.guests ?? []);
    const { children: childList, infants: infantList } =
      splitChildAndInfantGuests(allGuests);
    const { children, infants } = transformGuestsForSearch(
      childList,
      infantList,
    );
    return {
      adults: allGuests.filter((g) => g.ageType === "adult").length,
      teenagers: allGuests.filter((g) => g.ageType === "teenager").length,
      children,
      infants,
    };
  }, [draftUnits]);

  const { data: packageDetail, isFetching: isFetchingPackageDetail } =
    useGetPackageDetail({
      variables: {
        siteId,
        packageAdId: packageAdId ?? "",
        arrivalDate: roomInfoCtx?.startDate
          ? formatDateForAPI(roomInfoCtx.startDate)
          : "",
        departureDate: roomInfoCtx?.endDate
          ? formatDateForAPI(roomInfoCtx.endDate)
          : "",
        guests: packageDetailGuests,
        languageCode: selectedLanguage,
        preview: previewVariable(isPackagePreviewMode),
      },
      enabled: isPackageMode && !!siteId && !!packageAdId && !!roomInfoCtx,
      placeholderData: keepPreviousData,
    });

  // Same query, keyed on the drafts the guest is about to commit: it warms the
  // exact cache entry the committed query will ask for, so the category step
  // opens with data instead of a skeleton. Only while they are one tap away.
  useGetPackageDetail({
    variables: {
      siteId,
      packageAdId: packageAdId ?? "",
      arrivalDate: draftStartDate ? formatDateForAPI(draftStartDate) : "",
      departureDate: draftEndDate ? formatDateForAPI(draftEndDate) : "",
      guests: draftPackageDetailGuests,
      languageCode: selectedLanguage,
      preview: previewVariable(isPackagePreviewMode),
    },
    enabled:
      isPackageMode &&
      step === "guests" &&
      !!siteId &&
      !!packageAdId &&
      !!draftStartDate &&
      !!draftEndDate,
  });

  // A package is priced per occupancy, so the summed-guests detail above prices
  // the party as one room and cannot say what each unit costs. Ask per unit,
  // exactly as the desktop dialog does — same hook, same keys, so this reads
  // the cache the dialog already warmed rather than adding round-trips.
  const packageGuestsByUnit = useMemo<GuestDetails[]>(
    () => committedUnits.map((unit) => guestDetailsFromUnit(unit)),
    [committedUnits],
  );

  const packageDetailQueriesByUnit = useGetPackageDetailsByUnits({
    siteId,
    packageAdId: packageAdId ?? "",
    arrivalDate: roomInfoCtx?.startDate
      ? formatDateForAPI(roomInfoCtx.startDate)
      : undefined,
    departureDate: roomInfoCtx?.endDate
      ? formatDateForAPI(roomInfoCtx.endDate)
      : undefined,
    guestsByUnit: packageGuestsByUnit,
    languageCode: selectedLanguage,
    enabled: isPackageMode && !!siteId && !!packageAdId && !!roomInfoCtx,
    preview: previewVariable(isPackagePreviewMode),
  });

  // These keep previous data while refetching, so a guest-count change leaves
  // the old price on screen until the new one lands. Same gate the dialog
  // applies, so the sheet never invites a tap on a stale figure.
  const isFetchingUnitDetails = packageDetailQueriesByUnit.some(
    (query) => query.isFetching,
  );

  const packageDetailByUnitId = useMemo(
    () =>
      new Map(
        committedUnits.map((unit, index) => [
          unit.id,
          packageDetailQueriesByUnit[index]?.data,
        ]),
      ),
    [committedUnits, packageDetailQueriesByUnit],
  );

  const packageCategories = useMemo(
    () => sortCategoriesByAvailability(packageDetail?.categories ?? []),
    [packageDetail],
  );

  /** The categories to show a given unit: its own priced copy when it has
   *  landed, the shared detail's until then. */
  const categoriesForUnit = useCallback(
    (unitId: string) =>
      sortCategoriesByAvailability(
        packageDetailByUnitId.get(unitId)?.categories ??
          packageDetail?.categories ??
          [],
      ),
    [packageDetailByUnitId, packageDetail?.categories],
  );

  // A package bound to a single category chooses itself — no step to show.
  const hasCategoryStep =
    isPackageMode &&
    !!roomInfoCtx &&
    !shouldAutoSkipCategoryChoice(packageDetail) &&
    packageCategories.length > 0;

  /** The category step needs a stay to price and check availability against,
   *  so it stays inert until the guest has both ends of the range. */
  const categoryStepUnlocked = !!selectedStartDate && !!selectedEndDate;

  const chosenCategoryByUnit = roomInfoCtx?.chosenCategoryByUnit ?? {};
  const committedUnitIds = committedUnits.map((u) => u.id);
  const activeChoiceUnitId = getNextUnitNeedingChoice(
    committedUnitIds,
    chosenCategoryByUnit,
  );
  const allCategoriesChosen = areAllUnitsChosen(
    committedUnitIds,
    chosenCategoryByUnit,
  );

  /**
   * The choice that completes the set commits by itself.
   *
   * The category chooser is hosted inside this sheet, so it inherited the
   * sheet's generic confirm — a step whose only content was "Uppdatera" sitting
   * between the guest picking a room and the package reaching the cart. Dates
   * and guests need that confirm: several values are drafted, then committed
   * together. A category does not — the tap IS the decision, and for a
   * multi-unit package the last tap is the decision for all of them.
   *
   * Fires only on the false -> true transition, so a sheet reopened on an
   * already-complete selection stays open: the guest opened it to change
   * something, and closing it under them would be the same step in reverse.
   */
  const wasAllCategoriesChosen = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isOpen || !hasCategoryStep || step !== "category") {
      wasAllCategoriesChosen.current = null;
      return;
    }
    const previous = wasAllCategoriesChosen.current;
    wasAllCategoriesChosen.current = allCategoriesChosen;
    if (previous === false && allCategoriesChosen) {
      void handleSearch();
    }
    // `handleSearch` is redefined every render; the transition above is what
    // gates this, not the identity of the committer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasCategoryStep, step, allCategoriesChosen]);

  const categoryById = useMemo(
    () => new Map(packageCategories.map((c) => [c.categoryAdId, c] as const)),
    [packageCategories],
  );

  const categorySummary = committedUnitIds
    .map((id) => {
      const chosen = chosenCategoryByUnit[id];
      return chosen ? categoryById.get(chosen)?.name : null;
    })
    .filter(Boolean)
    .join(", ");

  const guestCountsForUnit = (unit: SearchUnit): UnitGuestCounts => {
    const counts: UnitGuestCounts = {
      adult: 0,
      teenager: 0,
      child: 0,
      infant: 0,
    };
    unit.guests.forEach((g) => {
      counts[g.ageType] += 1;
    });
    return counts;
  };

  /** Push the sheet's drafts into the dialog's context without closing. */
  /** The id to hand outward on commit. `draftActiveUnitId` is empty while the
   *  guest has every unit accordion folded shut, which is a statement about
   *  this sheet's UI — the rest of the app still needs a real unit to edit, so
   *  the collapsed state never leaves here. */
  const committedActiveUnitId =
    draftActiveUnitId || draftUnits[0]?.id || activeUnitId;

  const commitDialogDrafts = useCallback(() => {
    if (!roomInfoCtx) return;
    roomInfoCtx.setStartDate(draftStartDate);
    roomInfoCtx.setEndDate(draftEndDate);
    roomInfoCtx.setUnits(draftUnits);
    roomInfoCtx.setActiveUnitId(committedActiveUnitId);
  }, [
    roomInfoCtx,
    draftStartDate,
    draftEndDate,
    draftUnits,
    committedActiveUnitId,
  ]);

  /**
   * A staged format change reshapes the rest of the form, so the parts it
   * invalidates are corrected here rather than left for the commit to reject:
   * activities book a single day and one group, stays need at least a night.
   */
  const handleSelectFormat = (module: AdFormat) => {
    setDraftFormat(module);
    const nextIsActivity = module === "activity";

    if (draftStartDate && draftEndDate) {
      if (nextIsActivity && !isSameDay(draftStartDate, draftEndDate)) {
        setDraftEndDate(draftStartDate);
      } else if (!nextIsActivity && isSameDay(draftStartDate, draftEndDate)) {
        setDraftEndDate(addDays(draftStartDate, 1));
      }
    }

    if (nextIsActivity && draftUnits.length > 1) {
      const kept =
        draftUnits.find((unit) => unit.id === draftActiveUnitId) ?? draftUnits[0];
      setDraftUnits([kept]);
      setDraftActiveUnitId(kept.id);
    }
  };

  const handleClose = () => {
    // Search mode buffers edits in local draft state — closing without
    // tapping "Uppdatera" leaves context untouched, so neither the URL nor
    // the search results change. roomInfo mode edits its own context
    // directly, mirroring the dialog's behavior on desktop.
    onClose();
  };

  const handleSearch = async () => {
    if (!selectedStartDate || !selectedEndDate) return;
    // Mirror the button guard: infants-only is never bookable, so it can never
    // be searched or committed from this sheet.
    if (hasInfantsOnlyUnit) return;
    if (isSearchMode && unitCapReached(booked.size, unitCap)) {
      toasty({ title: t("booking.max_units_hint"), variant: "error" });
      return;
    }

    if (useCtxDialog && roomInfoCtx) {
      // Commit drafts into RoomInfoSelectionContext so the dialog beneath
      // (roomInfo or package) sees the new selection. No further search call —
      // the dialog has its own queries gated on its context.
      commitDialogDrafts();
      onClose();
      return;
    }

    const stayRangeBlockedByStopTime = isStayRangeBlockedByStopTime(
      selectedStartDate,
      selectedEndDate,
      activeBookingAvailability,
    );

    if (stayRangeBlockedByStopTime) {
      const blockedMessage =
        getStopTimeMessageForStayRange(
          selectedStartDate,
          selectedEndDate,
          activeBookingAvailability,
          getLanguageCode(),
        ) ?? t("warning.stop_time_blocked_description");

      toasty({
        title: t("warning.stop_time_blocked_title"),
        description: blockedMessage,
        variant: "warning",
      });
      return;
    }

    // Reconcile the booking code into the store FIRST so the post-commit
    // useSearch read (which keys off the store's applied code) sees the right
    // value before the dates/guests flush + onSearch fire.
    if (isSearchMode && configuration?.acceptBookingCodes) {
      await commitBookingCode(draftBookingCode);
    }

    // Commit drafts to HeaderContext synchronously so the downstream
    // onSearch() reads the post-commit context state on the same tick —
    // without flushSync, useSearch.handleSearch would still see stale nulls.
    if (isSearchMode) {
      flushSync(() => {
        setSelectedStartDate(draftStartDate);
        setSelectedEndDate(draftEndDate);
        // The booked units stay as they are, in place; the draft replaces only the rest.
        replaceUnits([...units.filter((u) => booked.has(u.id)), ...draftUnits]);
        // The first unit of the search, not the one whose accordion happened to be open —
        // that is the last one added, and the plan then started on it instead of unit 1.
        // Same as the desktop bar.
        setActiveUnitId(draftUnits[0]?.id ?? committedActiveUnitId);
      });
    }

    // Warm the lodging category search; activity sites have no category ads
    // (the endpoint rejects the site) — their results fetch per activity ad.
    if (!isActivitySite) {
      await refetch();
    }
    // The format goes into the URL BEFORE the search rewrites it. The search starts a new
    // plan by carrying `window.location.search` forward, and the flow reads an absent
    // `module` as the site's default — so a format committed after it was thrown away, and
    // "Nästa" under the package tab landed on the room grid.
    if (isSearchMode && draftFormat && draftFormat !== committedFormat) {
      selectFormat(draftFormat);
    }
    handleClose();
    onSearch?.();
  };

  if (!isVisible) return null;

  return (
    <div
      className={clsx(
        "fixed inset-0 z-50 bg-scrim transition-opacity duration-200 ease-out lg:hidden",
        isAnimating ? "opacity-100" : "opacity-0",
      )}
      onClick={handleClose}
    >
      <div
        className={clsx(
          "absolute bottom-0 left-0 right-0 flex max-h-[90vh] flex-col rounded-t-2xl bg-surface-card-secondary transition-transform duration-200 ease-out",
          isAnimating ? "translate-y-0" : "translate-y-full",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-4">
            {/* Format choice — renders nothing unless the site offers more than one */}
            {isSearchMode && (
              <AdFormatTabs value={draftFormat} onSelect={handleSelectFormat} />
            )}

            {/* Date Selection */}
            <div
              className={clsx(
                "border-highlighted-backplate rounded-3xl border bg-surface-card shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]",
                step !== "dates" &&
                  "cursor-pointer transition-colors duration-200 hover:border-ink-disabled",
              )}
              onClick={() => step !== "dates" && setStep("dates")}
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold">
                    {t("search_sheet.date")}
                  </span>
                  {selectedStartDate && selectedEndDate ? (
                    <span className="text-sm">{formatDateRange()}</span>
                  ) : (
                    <span className="text-sm text-ink-secondary">
                      {shouldPromptDepartureAndGuests
                        ? t("header.select_departure_and_guests")
                        : t("search_sheet.select_date")}
                    </span>
                  )}
                </div>
                <motion.div
                  initial={false}
                  animate={{
                    height: step === "dates" ? "auto" : 0,
                    opacity: step === "dates" ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className={step === "dates" ? "overflow-visible" : "overflow-hidden"}
                  aria-hidden={step !== "dates"}
                >
                  <div className="mt-4 px-0.5 pb-0.5">
                    <Calendar
                      {...calendarLocalization}
                      nested
                      selectedStartDate={selectedStartDate}
                      selectedEndDate={selectedEndDate}
                      onDateSelect={onDateSelect}
                      currentMonth={currentMonth}
                      onMonthChange={setCurrentMonth}
                      variant="single"
                      bookingAvailability={activeBookingAvailability}
                      isLoading={isAvailabilityStale}
                      isRefreshing={isAvailabilityRefreshing}
                      requireRange={!!onRangeChange}
                      singleDaySelect={isActivitySite}
                      onRangeChange={onRangeChange}
                    />
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Guest Selection */}
            <div
              className={clsx(
                "border-highlighted-backplate rounded-2xl border bg-surface-card shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]",
                step !== "guests" &&
                  "cursor-pointer transition-colors duration-200 hover:border-ink-disabled",
              )}
              onClick={() => step !== "guests" && setStep("guests")}
            >
              <div className="">
                <div
                  className={clsx(
                    "flex items-center justify-between p-4",
                    step === "guests" && "pb-0",
                  )}
                >
                  <span className="text-base font-bold">
                    {t(isActivitySite ? "search_sheet.participants" : "search_sheet.guests")}
                  </span>
                  <span className="text-sm">
                    {formatGuestInfo() || t("search_sheet.select_guests")}
                  </span>
                </div>
                <motion.div
                  initial={false}
                  animate={{
                    height: step === "guests" ? "auto" : 0,
                    opacity: step === "guests" ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="overflow-hidden"
                  aria-hidden={step !== "guests"}
                >
                  <div className="mt-4">
                    {isRoomInfoMode ? (
                      <div className="border-highlighted-backplate rounded-b-2xl border-b bg-surface-card">
                        <div className="divide-highlighted-backplate divide-y">
                          {effectiveUnits.map((unit, idx) => {
                            const isActive = unit.id === effectiveActiveUnitId;
                            const body = (
                              <>
                                <div className="px-0 pb-2">
                                  <GuestSelect
                                    fromMobileSearchSheet
                                    adults={adultCount}
                                    teenagers={teenagerCount}
                                    children={childCount}
                                    infants={infantCount}
                                    childGuests={childGuests}
                                    infantGuests={infantGuests}
                                    onAdultsChange={onAdultsChange}
                                    onTeenagersChange={onTeenagersChange}
                                    onChildrenChange={onChildrenChange}
                                    onInfantsChange={onInfantsChange}
                                    onChildAgeChange={onChildAgeChange}
                                    onInfantAgeChange={onInfantAgeChange}
                                    guestLimits={guestLimits}
                                    totalGuests={totalGuests}
                                    maxTotalGuests={maxTotalGuests}
                                    guestTypesConfig={guestTypesConfig}
                                    labels={guestSelectLabels}
                                    disabled={isSelectionPending}
                                  />
                                </div>
                                {effectiveUnits.length > 1 && (
                                  <div className="px-4 pb-3">
                                    <Button
                                      variant="secondary"
                                      size="small"
                                      className="!h-8 !rounded-full"
                                      onClick={() =>
                                        handleRemoveRoomInfoUnit(unit.id)
                                      }
                                      disabled={isSelectionPending}
                                    >
                                      {t("booking.remove")}
                                    </Button>
                                  </div>
                                )}
                              </>
                            );
                            return (
                              <div key={unit.id}>
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between px-5 py-4 text-left"
                                  onClick={() => toggleActiveUnit(unit.id)}
                                >
                                  <span className="text-sm font-medium text-ink-secondary">
                                    {t(isActivitySite ? "booking.group" : "booking.unit", { number: idx + 1 })}
                                  </span>
                                  <FiChevronRight
                                    className={clsx(
                                      "h-5 w-5 transition-transform duration-200",
                                      isActive && "rotate-90",
                                    )}
                                  />
                                </button>
                                <AnimatePresence initial={false}>
                                  {isActive && (
                                    <motion.div
                                      key="body"
                                      className="overflow-hidden"
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: skipAccordionTransition ? 0 : 0.25, ease: "easeOut" }}
                                    >
                                      {body}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                        <div className="border-highlighted-backplate border-t p-3">
                          <Button
                            variant="secondary"
                            size="large"
                            onClick={() => {
                              if (!canAddRoomInfoUnitWithAvailability) return;
                              flushSync(() => setIsInstantUnitChange(true));
                              handleAddRoomInfoUnit();
                            }}
                            className="w-full !rounded-full"
                            disabled={
                              !canAddRoomInfoUnitWithAvailability ||
                              isSelectionPending
                            }
                          >
                            <FiPlus className="mr-2 h-4 w-4" />
                            {t(isActivitySite ? "booking.add_group" : "booking.add_unit")}
                          </Button>
                        </div>
                      </div>
                    ) : maxUnits === 1 ? (
                      // One group only (activity sites): no group accordion,
                      // no dead add button — just the guest steppers.
                      <div className="border-highlighted-backplate rounded-b-2xl border-b bg-surface-card pb-2">
                        <GuestSelect
                          fromMobileSearchSheet
                          adults={adultCount}
                          teenagers={teenagerCount}
                          children={childCount}
                          infants={infantCount}
                          childGuests={childGuests}
                          infantGuests={infantGuests}
                          onAdultsChange={onAdultsChange}
                          onTeenagersChange={onTeenagersChange}
                          onChildrenChange={onChildrenChange}
                          onInfantsChange={onInfantsChange}
                          onChildAgeChange={onChildAgeChange}
                          onInfantAgeChange={onInfantAgeChange}
                          guestLimits={guestLimits}
                          totalGuests={totalGuests}
                          maxTotalGuests={maxTotalGuests}
                          guestTypesConfig={guestTypesConfig}
                          labels={guestSelectLabels}
                        />
                      </div>
                    ) : (
                      <div className="border-highlighted-backplate rounded-b-2xl border-b bg-surface-card">
                        <div className="divide-highlighted-backplate divide-y">
                          {effectiveUnits.map((unit, idx) => {
                            const isActive = unit.id === effectiveActiveUnitId;
                            const body = (
                              <>
                                <div className="px-0 pb-2">
                                  <GuestSelect
                                    fromMobileSearchSheet
                                    adults={adultCount}
                                    teenagers={teenagerCount}
                                    children={childCount}
                                    infants={infantCount}
                                    childGuests={childGuests}
                                    infantGuests={infantGuests}
                                    onAdultsChange={onAdultsChange}
                                    onTeenagersChange={onTeenagersChange}
                                    onChildrenChange={onChildrenChange}
                                    onInfantsChange={onInfantsChange}
                                    onChildAgeChange={onChildAgeChange}
                                    onInfantAgeChange={onInfantAgeChange}
                                    guestLimits={guestLimits}
                                    totalGuests={totalGuests}
                                    maxTotalGuests={maxTotalGuests}
                                    guestTypesConfig={guestTypesConfig}
                                    labels={guestSelectLabels}
                                    disabled={isSelectionPending}
                                  />
                                </div>
                                {effectiveUnits.length > 1 && (
                                  <div className="px-4 pb-3">
                                    <Button
                                      variant="secondary"
                                      size="small"
                                      className="!h-8 !rounded-full"
                                      onClick={() =>
                                        handleSearchRemoveUnit(unit.id)
                                      }
                                      disabled={isSelectionPending}
                                    >
                                      {t("booking.remove")}
                                    </Button>
                                  </div>
                                )}
                              </>
                            );
                            return (
                              <div key={unit.id}>
                                <button
                                  type="button"
                                  className="flex w-full items-center justify-between px-5 py-4 text-left"
                                  onClick={() => toggleActiveUnit(unit.id)}
                                >
                                  <span className="text-sm font-medium text-ink-secondary">
                                    {t(isActivitySite ? "booking.group" : "booking.unit", { number: idx + 1 })}
                                  </span>
                                  <FiChevronRight
                                    className={clsx(
                                      "h-5 w-5 transition-transform duration-200",
                                      isActive && "rotate-90",
                                    )}
                                  />
                                </button>
                                <AnimatePresence initial={false}>
                                  {isActive && (
                                    <motion.div
                                      key="body"
                                      className="overflow-hidden"
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: skipAccordionTransition ? 0 : 0.25, ease: "easeOut" }}
                                    >
                                      {body}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                        <div className="border-highlighted-backplate border-t p-3">
                          <Button
                            variant="secondary"
                            size="large"
                            onClick={() => {
                              flushSync(() => setIsInstantUnitChange(true));
                              handleSearchAddUnit();
                            }}
                            className="w-full !rounded-full"
                            disabled={
                              (typeof maxUnits === "number" &&
                                effectiveUnits.length >= maxUnits) ||
                              isSelectionPending
                            }
                          >
                            <FiPlus className="mr-2 h-4 w-4" />
                            {t(isActivitySite ? "booking.add_group" : "booking.add_unit")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Category Selection — package mode only.
                Locked until the stay is complete: the cards carry a price and
                an availability verdict, both of which are answered for a date
                range. Opened without one, the guest picks a room from cards
                that cannot say whether it is free or what it costs. The Next
                button into this step already required dates; this shortcut
                into it has to require the same. */}
            {hasCategoryStep && (
              <div
                ref={categorySectionRef}
                className={clsx(
                  "border-highlighted-backplate rounded-2xl border bg-surface-card shadow-[0px_2px_8px_0px_rgba(0,0,0,0.06)]",
                  !categoryStepUnlocked && "opacity-50",
                  categoryStepUnlocked &&
                    step !== "category" &&
                    "cursor-pointer transition-colors duration-200 hover:border-ink-disabled",
                )}
                onClick={() =>
                  categoryStepUnlocked &&
                  step !== "category" &&
                  setStep("category")
                }
                aria-disabled={!categoryStepUnlocked}
                data-testid="search-sheet-category"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-base font-bold">
                      {t("search_sheet.category")}
                    </span>
                    <span
                      className={clsx(
                        "min-w-0 truncate text-sm",
                        !categorySummary && "text-ink-secondary",
                      )}
                    >
                      {categorySummary || t("search_sheet.select_category")}
                    </span>
                  </div>
                  <motion.div
                    initial={false}
                    animate={{
                      height: step === "category" ? "auto" : 0,
                      opacity: step === "category" ? 1 : 0,
                    }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="overflow-hidden"
                    aria-hidden={step !== "category"}
                  >
                    <div className="mt-4 flex flex-col gap-3">
                      {/* Skeleton until BOTH the shared detail and the
                          per-unit prices have landed — the cards carry a price,
                          and showing the party-wide one first would quote a
                          figure that then changes under the guest's thumb. */}
                      {isFetchingPackageDetail || isFetchingUnitDetails ? (
                        <Skeleton className="h-40 w-full rounded-2xl" />
                      ) : (
                        <>
                          <AnimatePresence initial={false}>
                            {committedUnits.map((unit, idx) => {
                              const chosenId = chosenCategoryByUnit[unit.id];
                              const chosen = chosenId
                                ? categoryById.get(chosenId)
                                : undefined;
                              if (!chosen) return null;
                              return (
                                <motion.div
                                  key={unit.id}
                                  data-testid="search-sheet-category-chosen"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{
                                    duration: 0.22,
                                    ease: "easeOut",
                                  }}
                                  className="overflow-hidden"
                                >
                                  <PackageRoomRow
                                    name={chosen.name}
                                    description={chosen.description}
                                    image={
                                      chosen.image ?? chosen.images[0] ?? null
                                    }
                                    label={
                                      committedUnits.length > 1
                                        ? t(isActivitySite ? "booking.group" : "booking.unit", { number: idx + 1 })
                                        : null
                                    }
                                    onChange={() =>
                                      roomInfoCtx?.setChosenCategoryByUnit(
                                        (prev) => ({
                                          ...prev,
                                          [unit.id]: null,
                                        }),
                                      )
                                    }
                                  />
                                </motion.div>
                              );
                            })}
                          </AnimatePresence>

                          <AnimatePresence initial={false}>
                            {committedUnits
                              .filter((unit) => unit.id === activeChoiceUnitId)
                              .map((unit) => (
                                <PackageCategoryChoice
                                  key={unit.id}
                                  categories={categoriesForUnit(unit.id)}
                                  guests={guestCountsForUnit(unit)}
                                  chosenCategoryAdId={null}
                                  onChoose={(categoryAdId) =>
                                    roomInfoCtx?.setChosenCategoryByUnit(
                                      (prev) => ({
                                        ...prev,
                                        [unit.id]: categoryAdId,
                                      }),
                                    )
                                  }
                                  onClear={() =>
                                    roomInfoCtx?.setChosenCategoryByUnit(
                                      (prev) => ({ ...prev, [unit.id]: null }),
                                    )
                                  }
                                  hideHeading
                                  takenByOtherUnits={countChoicesExcluding(
                                    roomInfoCtx?.chosenCategoryByUnit ?? {},
                                    unit.id,
                                  )}
                                  unitPosition={{
                                    current:
                                      committedUnits.findIndex(
                                        (u) => u.id === unit.id,
                                      ) + 1,
                                    total: committedUnits.length,
                                  }}
                                />
                              ))}
                          </AnimatePresence>
                        </>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>
            )}

            {/* Booking-code block — input + Apply + inline rules card. Shown
                whenever the site accepts codes (search + room-info modes). */}
            {showBookingCodeBlock && (
              <MobileBookingCodeBlock
                siteId={siteId}
                draft={draftBookingCode}
                onDraftChange={setDraftBookingCode}
                showRules={configuration?.showBookingCodeInfo === true}
              />
            )}
          </div>
        </div>

        {/* Fixed Bottom Buttons */}
        <div className="border-highlighted-backplate space-y-3 border-t bg-surface-card-secondary p-4">
          <Button
            variant="secondary"
            size="large"
            onClick={handleClose}
            className="w-full"
          >
            {t("common.close")}
          </Button>
          {step === "dates" ? (
            <Button
              variant="secondary"
              size="large"
              onClick={() => setStep("guests")}
              className="w-full !bg-primary !text-on-primary hover:!bg-primary/90"
              disabled={
                !selectedStartDate || !selectedEndDate || isSelectionPending
              }
              isLoading={isSelectionPending}
            >
              {t("common.next")}
            </Button>
          ) : step === "guests" && hasCategoryStep ? (
            <Button
              variant="secondary"
              size="large"
              onClick={() => {
                // Commit before showing categories: their prices and
                // availability are fetched for the committed stay, so an
                // uncommitted draft would show the guest the wrong cards.
                commitDialogDrafts();
                setStep("category");
              }}
              className="w-full !bg-primary !text-on-primary hover:!bg-primary/90"
              disabled={
                !selectedStartDate ||
                !selectedEndDate ||
                totalGuestsAcrossUnits === 0 ||
                hasInfantsOnlyUnit ||
                isSelectionPending
              }
              isLoading={isSelectionPending}
              data-testid="search-sheet-to-category"
            >
              {t("common.next")}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="large"
              onClick={handleSearch}
              className="w-full !rounded-full !bg-primary !text-on-primary hover:!bg-primary/90"
              disabled={
                !selectedStartDate ||
                !selectedEndDate ||
                (selectedStartDate &&
                  selectedEndDate &&
                  isStayRangeBlockedByStopTime(
                    selectedStartDate,
                    selectedEndDate,
                    activeBookingAvailability,
                  )) ||
                // Block empty bookings from any entry point — the header
                // search icon on the room-info view opens this sheet in plain
                // `search` mode and would otherwise let 0-guest clicks through.
                totalGuestsAcrossUnits === 0 ||
                // Infants-only is never bookable on any channel.
                hasInfantsOnlyUnit ||
                // Wait on the in-flight availability/pricing probes of
                // whichever feed this mode uses, so a stale snapshot can't be
                // confirmed mid-refetch.
                isSelectionPending ||
                // The category step is part of the package flow: closing it
                // half-done would leave the page beneath unbookable anyway.
                (hasCategoryStep && !allCategoriesChosen)
              }
              isLoading={isSelectionPending}
            >
              {t("booking.search")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
